// src/app/mocks/mock-chart-data.ts
import { NotificationItem } from '../models/notification.model';


export interface ChartDataItem {
  Id: number;
  Label: string;
  Value: number;
  Color: string;
  Percentage?: number;
  Target: number;
  Variance: number;
  Trend: string;
  DisplayOrder: number;
  IsRead: boolean;
}

export interface ChartConfig {
  Category: string;
  ChartType: string;
  Title: string;
  Description: string;
  DisplayOrder: number;
  ShowLegend: boolean;
  ShowPercentages: boolean;
  ValueSuffix: string;
  ColorScheme: string;
  Data: ChartDataItem[];
}

export interface ChartApiResponse {
  success: boolean;
  title?: string;
  body?: string;
  type?: string;
  created?: string;
  data: string | ChartConfig[];
  message?: string;
}

export class MockChartData {
  
  static readonly mockCharts: NotificationItem[] = [
    {
      notificationID: 101,
      title: 'Dashboard Q1 2025 - Finanční přehled',
      body: 'Komplexní finanční dashboard s porovnáním výkonnosti, trendy příjmů a analýzou nákladů za první čtvrtletí.',
      type: 3,
      created: '2025-03-20T08:30:00Z',
      senderUserID: 201,
      apnsPriority: 1,
      readAt: null,
      isRead: false
    },
    {
      notificationID: 102,
      title: 'Prodejní výkonnost podle regionů',
      body: 'Interaktivní grafy zobrazující prodejní výsledky v jednotlivých regionech včetně srovnání s cílovými hodnotami.',
      type: 3,
      created: '2025-03-18T14:15:00Z',
      senderUserID: 202,
      apnsPriority: 1,
      readAt: null,
      isRead: false
    },
    {
      notificationID: 103,
      title: 'Zákaznická spokojenost - Březen',
      body: 'Vizualizace metrik zákaznické spokojenosti, NPS skóre a analýza zpětné vazby za březen 2025.',
      type: 3,
      created: '2025-03-15T10:45:00Z',
      senderUserID: 203,
      apnsPriority: 1,
      readAt: '2025-03-20T15:00:00Z',
      isRead: true
    },
    {
      notificationID: 104,
      title: 'Marketingové kampaně - ROI analýza',
      body: 'Grafické zobrazení návratnosti investic do marketingových kampaní s breakdown podle kanálů.',
      type: 3,
      created: '2025-03-12T16:20:00Z',
      senderUserID: 204,
      apnsPriority: 1,
      readAt: null,
      isRead: false
    },
    {
      notificationID: 105,
      title: 'Provozní metriky a KPI',
      body: 'Přehledové grafy klíčových provozních ukazatelů, efektivity procesů a výkonnostních metrik.',
      type: 3,
      created: '2025-03-08T11:30:00Z',
      senderUserID: 205,
      apnsPriority: 1,
      readAt: null,
      isRead: false
    }
  ];

  private static readonly mockChartDetailData: Record<string, ChartApiResponse> = {
    '101': {
      success: true,
      title: 'Dashboard Q1 2025 - Finanční přehled',
      body: 'Komplexní finanční dashboard s porovnáním výkonnosti',
      type: 'financial',
      created: '2025-03-20T08:30:00Z',
      data: [
        {
          Category: 'Příjmy',
          ChartType: 'bar',
          Title: 'Měsíční příjmy Q1 2025',
          Description: 'Porovnání měsíčních příjmů s plánovanými cíli',
          DisplayOrder: 1,
          ShowLegend: true,
          ShowPercentages: true,
          ValueSuffix: ' Kč',
          ColorScheme: 'financial',
          Data: [
            { Id: 1, Label: 'Leden', Value: 2850000, Color: '#2E8B57', Percentage: 95.0, Target: 3000000, Variance: -150000, Trend: 'below', DisplayOrder: 1,  IsRead: false },
            { Id: 2, Label: 'Únor', Value: 3200000, Color: '#32CD32', Percentage: 106.7, Target: 3000000, Variance: 200000, Trend: 'above', DisplayOrder: 2,  IsRead: false },
            { Id: 3, Label: 'Březen', Value: 3450000, Color: '#228B22', Percentage: 115.0, Target: 3000000, Variance: 450000, Trend: 'above', DisplayOrder: 3,  IsRead: false }
          ]
        },
        {
          Category: 'Náklady',
          ChartType: 'line',
          Title: 'Vývoj nákladů',
          Description: 'Trend nákladů oproti rozpočtu',
          DisplayOrder: 2,
          ShowLegend: true,
          ShowPercentages: false,
          ValueSuffix: ' Kč',
          ColorScheme: 'expense',
          Data: [
            { Id: 1, Label: 'Provozní', Value: 1250000, Color: '#FF6B6B', Percentage: 83.3, Target: 1500000, Variance: -250000, Trend: 'below', DisplayOrder: 1,  IsRead: false },
            { Id: 2, Label: 'Personální', Value: 1800000, Color: '#FF8E53', Percentage: 90.0, Target: 2000000, Variance: -200000, Trend: 'below', DisplayOrder: 2,  IsRead: false },
            { Id: 3, Label: 'Marketing', Value: 450000, Color: '#FF4757', Percentage: 112.5, Target: 400000, Variance: 50000, Trend: 'above', DisplayOrder: 3,  IsRead: false }
          ]
        },
        {
          Category: 'Zisk',
          ChartType: 'summary',
          Title: 'Čistý zisk Q1',
          Description: 'Celkový přehled ziskovosti',
          DisplayOrder: 3,
          ShowLegend: false,
          ShowPercentages: true,
          ValueSuffix: ' Kč',
          ColorScheme: 'profit',
          Data: [
            { Id: 1, Label: 'Celkový zisk', Value: 6000000, Color: '#4ECDC4', Percentage: 120.0, Target: 5000000, Variance: 1000000, Trend: 'above', DisplayOrder: 1,  IsRead: false }
          ]
        }
      ]
    },
    '102': {
      success: true,
      title: 'Prodejní výkonnost podle regionů',
      body: 'Interaktivní grafy zobrazující prodejní výsledky v jednotlivých regionech',
      type: 'sales',
      created: '2025-03-18T14:15:00Z',
      data: [
        {
          Category: 'Regiony',
          ChartType: 'bar',
          Title: 'Prodeje podle regionů',
          Description: 'Srovnání prodejní výkonnosti jednotlivých regionů',
          DisplayOrder: 1,
          ShowLegend: true,
          ShowPercentages: true,
          ValueSuffix: ' ks',
          ColorScheme: 'regions',
          Data: [
            { Id: 1, Label: 'Praha', Value: 1250, Color: '#667EEA', Percentage: 125.0, Target: 1000, Variance: 250, Trend: 'above', DisplayOrder: 1,  IsRead: false },
            { Id: 2, Label: 'Brno', Value: 890, Color: '#764BA2', Percentage: 89.0, Target: 1000, Variance: -110, Trend: 'below', DisplayOrder: 2,  IsRead: false },
            { Id: 3, Label: 'Ostrava', Value: 670, Color: '#F093FB', Percentage: 83.8, Target: 800, Variance: -130, Trend: 'below', DisplayOrder: 3,  IsRead: false },
            { Id: 4, Label: 'Plzeň', Value: 520, Color: '#F9844A', Percentage: 104.0, Target: 500, Variance: 20, Trend: 'above', DisplayOrder: 4,  IsRead: false }
          ]
        },
        {
          Category: 'Produkty',
          ChartType: 'line',
          Title: 'Top produkty',
          Description: 'Nejprodávanější produkty napříč regiony',
          DisplayOrder: 2,
          ShowLegend: true,
          ShowPercentages: false,
          ValueSuffix: ' ks',
          ColorScheme: 'products',
          Data: [
            { Id: 1, Label: 'Produkt A', Value: 850, Color: '#11998E', Percentage: 106.3, Target: 800, Variance: 50, Trend: 'above', DisplayOrder: 1,  IsRead: false },
            { Id: 2, Label: 'Produkt B', Value: 720, Color: '#38EF7D', Percentage: 90.0, Target: 800, Variance: -80, Trend: 'below', DisplayOrder: 2,  IsRead: false },
            { Id: 3, Label: 'Produkt C', Value: 680, Color: '#0575E6', Percentage: 113.3, Target: 600, Variance: 80, Trend: 'above', DisplayOrder: 3,  IsRead: false }
          ]
        }
      ]
    },
    '103': {
      success: true,
      title: 'Zákaznická spokojenost - Březen',
      body: 'Vizualizace metrik zákaznické spokojenosti a NPS skóre',
      type: 'satisfaction',
      created: '2025-03-15T10:45:00Z',
      data: [
        {
          Category: 'Spokojenost',
          ChartType: 'summary',
          Title: 'NPS Score',
          Description: 'Net Promoter Score za březen',
          DisplayOrder: 1,
          ShowLegend: false,
          ShowPercentages: false,
          ValueSuffix: '',
          ColorScheme: 'nps',
          Data: [
            { Id: 1, Label: 'NPS', Value: 67, Color: '#4ECDC4', Percentage: 111.7, Target: 60, Variance: 7, Trend: 'above', DisplayOrder: 1, IsRead: false }
          ]
        },
        {
          Category: 'Hodnocení',
          ChartType: 'bar',
          Title: 'Hodnocení služeb',
          Description: 'Průměrné hodnocení jednotlivých služeb',
          DisplayOrder: 2,
          ShowLegend: true,
          ShowPercentages: true,
          ValueSuffix: '/5',
          ColorScheme: 'rating',
          Data: [
            { Id: 1, Label: 'Zákaznický servis', Value: 4.5, Color: '#45B7D1', Percentage: 90.0, Target: 5.0, Variance: -0.5, Trend: 'below', DisplayOrder: 1, IsRead: false },
            { Id: 2, Label: 'Kvalita produktu', Value: 4.7, Color: '#96CEB4', Percentage: 94.0, Target: 5.0, Variance: -0.3, Trend: 'below', DisplayOrder: 2, IsRead: false },
            { Id: 3, Label: 'Rychlost dodání', Value: 4.2, Color: '#FECA57', Percentage: 84.0, Target: 5.0, Variance: -0.8, Trend: 'below', DisplayOrder: 3, IsRead: false },
            { Id: 4, Label: 'Celková spokojenost', Value: 4.4, Color: '#FF9FF3', Percentage: 88.0, Target: 5.0, Variance: -0.6, Trend: 'below', DisplayOrder: 4, IsRead: false }
          ]
        }
      ]
    },
    '104': {
      success: true,
      title: 'Marketingové kampaně - ROI analýza',
      body: 'Grafické zobrazení návratnosti investic do marketingových kampaní',
      type: 'marketing',
      created: '2025-03-12T16:20:00Z',
      data: [
        {
          Category: 'ROI',
          ChartType: 'bar',
          Title: 'ROI podle kanálů',
          Description: 'Návratnost investic jednotlivých marketingových kanálů',
          DisplayOrder: 1,
          ShowLegend: true,
          ShowPercentages: true,
          ValueSuffix: '%',
          ColorScheme: 'roi',
          Data: [
            { Id: 1, Label: 'Google Ads', Value: 320, Color: '#FF6B6B', Percentage: 106.7, Target: 300, Variance: 20, Trend: 'above', DisplayOrder: 1, IsRead: false },
            { Id: 2, Label: 'Facebook', Value: 280, Color: '#4ECDC4', Percentage: 93.3, Target: 300, Variance: -20, Trend: 'below', DisplayOrder: 2, IsRead: false },
            { Id: 3, Label: 'Email Marketing', Value: 450, Color: '#45B7D1', Percentage: 150.0, Target: 300, Variance: 150, Trend: 'above', DisplayOrder: 3, IsRead: false },
            { Id: 4, Label: 'LinkedIn', Value: 180, Color: '#96CEB4', Percentage: 60.0, Target: 300, Variance: -120, Trend: 'below', DisplayOrder: 4, IsRead: false }
          ]
        },
        {
          Category: 'Dosah',
          ChartType: 'line',
          Title: 'Dosah kampaní',
          Description: 'Počet oslovených uživatelů podle kanálů',
          DisplayOrder: 2,
          ShowLegend: true,
          ShowPercentages: false,
          ValueSuffix: 'k',
          ColorScheme: 'reach',
          Data: [
            { Id: 1, Label: 'Organický dosah', Value: 125, Color: '#2ECC71', Percentage: 104.2, Target: 120, Variance: 5, Trend: 'above', DisplayOrder: 1, IsRead: false },
            { Id: 2, Label: 'Placený dosah', Value: 340, Color: '#E74C3C', Percentage: 113.3, Target: 300, Variance: 40, Trend: 'above', DisplayOrder: 2, IsRead: false },
            { Id: 3, Label: 'Virální dosah', Value: 85, Color: '#9B59B6', Percentage: 85.0, Target: 100, Variance: -15, Trend: 'below', DisplayOrder: 3, IsRead: false }
          ]
        }
      ]
    },
    '105': {
      success: true,
      title: 'Provozní metriky a KPI',
      body: 'Přehledové grafy klíčových provozních ukazatelů',
      type: 'operations',
      created: '2025-03-08T11:30:00Z',
      data: [
        {
          Category: 'Efektivita',
          ChartType: 'summary',
          Title: 'Celková efektivita',
          Description: 'Souhrnný ukazatel provozní efektivity',
          DisplayOrder: 1,
          ShowLegend: false,
          ShowPercentages: true,
          ValueSuffix: '%',
          ColorScheme: 'efficiency',
          Data: [
            { Id: 1, Label: 'Efektivita', Value: 87, Color: '#27AE60', Percentage: 96.7, Target: 90, Variance: -3, Trend: 'below', DisplayOrder: 1, IsRead: false }
          ]
        },
        {
          Category: 'Výkon týmů',
          ChartType: 'bar',
          Title: 'Výkonnost podle týmů',
          Description: 'Porovnání výkonnosti jednotlivých týmů',
          DisplayOrder: 2,
          ShowLegend: true,
          ShowPercentages: true,
          ValueSuffix: '%',
          ColorScheme: 'teams',
          Data: [
            { Id: 1, Label: 'Vývoj', Value: 92, Color: '#3498DB', Percentage: 102.2, Target: 90, Variance: 2, Trend: 'above', DisplayOrder: 1, IsRead: false },
            { Id: 2, Label: 'Marketing', Value: 88, Color: '#E67E22', Percentage: 97.8, Target: 90, Variance: -2, Trend: 'below', DisplayOrder: 2, IsRead: false },
            { Id: 3, Label: 'Prodej', Value: 95, Color: '#2ECC71', Percentage: 105.6, Target: 90, Variance: 5, Trend: 'above', DisplayOrder: 3, IsRead: false },
            { Id: 4, Label: 'Support', Value: 83, Color: '#9B59B6', Percentage: 92.2, Target: 90, Variance: -7, Trend: 'below', DisplayOrder: 4, IsRead: false }
          ]
        }
      ]
    }
  };

  static getChartList(): NotificationItem[] {
    return [...this.mockCharts];
  }

  static getChartDetail(chartId: string): ChartApiResponse {
    return this.mockChartDetailData[chartId] || {
      success: false,
      message: 'Chart not found',
      data: []
    };
  }

  static addMockChart(chart: NotificationItem): void {
    this.mockCharts.push(chart);
  }

  static resetData(): void {
    // TODO
  }
}